/* eslint-disable no-case-declarations */
import PageTitle from '../../components/PageTitle'
import Context from '../../context'
import GeneralImageHero from '../../components/GeneralImageHero'
import ProductCard from '../../components/ProductCard'
import React, { useState, useEffect, useContext, useRef } from 'react'
import getConfig from 'next/config'
import FilterCount from '../../components/FilterCount'
import useDidMountEffect from '../../utils/useDidMountEffect'
import ContentGroup from '../../components/ContentGroup'
import BreadCrumb from '../../components/BreadCrumb'
import BackToTop from '../../components/BackToTop'
import { useAuthAPI } from '../../hooks/api.hooks'
import SEOCopy from '../../components/SEOCopy'
import AEMContent, { AEMPageType } from '../../components/AEMContent'
import Widget from '../../components/Widget'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/keyboard'

import { useIonModal } from '@ionic/react'
import Filters from '../../components/Filters'
import { enterAnimation, leaveAnimation } from '../../utils/modal-animations'
import SVG from '../../components/SVG'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { setPageViewData } from '../../utils/analytics'

const { publicRuntimeConfig } = getConfig()

const Category = ({
  products,
  category,
  numberOfResults,
  widget,
  catSlug
}) => {
  const { state } = useContext(Context)
  const [productsArr, setProductsArr] = useState(products)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState({})
  const [totalRows, setTotalRows] = useState(numberOfResults)
  const [selectedFilters, setSelectedFilters] = useState({
    facets: {},
    sort: {}
  })
  const [animated, setAnimated] = useState(false)
  const [showFilterLoader, setFilterLoader] = useState(false)
  const [grid, setOpen] = useState(false)
  const isFirstRun = useRef(true)
  const router = useRouter()
  const authAPICall = useAuthAPI()
  const filtersSet = Object.keys(selectedFilters.facets).length > 0
  const isBRLoaded = useRef(false)
  const NEW_AND_NOW_PAGE_TYPE = 'New And Now'
  const isPageViewEventPushed = useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined' && !isBRLoaded.current) {
      const parseCatId = (catId) => {
        if (catId.split('.').length === 1) {
          return catId
        }
        return `${catId}|${parseCatId(
          catId.split('.').slice(0, -1).join('.')
        )}`
      }

      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.ptype = 'category'
      window.br_data.cat_id = category[0].id.replace(/\./g, '_')
      window.br_data.cat = parseCatId(category[0].id)
        .split('|')
        .reverse()
        .join('|')
        .replace(/\./g, '_')
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      window.br_data.user_id =
        state.user.UID ||
        state.deviceId || '';

      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID
          }.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
        isBRLoaded.current = true
      })()
    }
  }, [category[0]])

  useEffect(() => {
    if (category?.length) {
      if (!isPageViewEventPushed.current) {
        const pageType = category[0]?.breadcrumbs.length > 1 ? 'PLP' : 'Category'
        const path = category[0]?.breadcrumbs.map(bc => bc.slug)
        const pageName = `${pageType}|${path?.join('/')}`
        const siteSection = path?.join('/')

        if (siteSection === 'new-now') {
          setPageViewData(NEW_AND_NOW_PAGE_TYPE, NEW_AND_NOW_PAGE_TYPE, NEW_AND_NOW_PAGE_TYPE)
          isPageViewEventPushed.current = true
        } else {
          const currentSiteSection = pageType === 'Category' ? siteSection : null
          setPageViewData(pageName, pageType, currentSiteSection)
          isPageViewEventPushed.current = true
        }
      } else {
        isPageViewEventPushed.current = false
      }
    }
  }, [router.pathname])

  const removeFilterKeys = (facets) => {
    delete facets.avg_rating
    delete facets.rating_count
    return facets
  }

  useEffect(() => {
    setOffset(0)
    setFilters({})

    if (isFirstRun.current) {
      isFirstRun.current = false
    } else {
      setSelectedFilters({
        facets: {},
        sort: {}
      })
    }

    authAPICall(
      'productinfo',
      'categoryproducts',
      'GET',
      {
        slug: catSlug,
        popkey: publicRuntimeConfig.POP_KEY,
        offset: 0,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      },
      {}
    ).then((data) => {
      setProductsArr([...data.response])
      const newFacets = removeFilterKeys({ ...data.facets })
      setFilters(newFacets)
      setTotalRows(data.numFound)
    })
  }, [state.cart.store?.storenumber])

  useEffect(() => {
    if (offset !== 0) {
      const filters = JSON.stringify(selectedFilters.facets)
      const sorting = JSON.stringify(selectedFilters.sort)

      authAPICall(
        'productinfo',
        'categoryproducts',
        'GET',
        {
          slug: catSlug,
          popkey: publicRuntimeConfig.POP_KEY,
          offset,
          store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
          filters,
          sort: sorting
        },
        {}
      ).then((data) => {
        setAnimated(false)
        setProductsArr([...productsArr, ...data.response])
        setTotalRows(data.numFound)
        const stringifyFirstProduct = String(data.response[0].slug)
        const firstProduct = document.getElementById(stringifyFirstProduct)
        firstProduct.querySelector('a').focus()
        firstProduct.scrollIntoView({
          behavior: 'smooth'
        })
      })
    }
  }, [offset])

  useDidMountEffect(() => {
    setOffset(0)
    setFilterLoader(true)
    const filters = JSON.stringify(selectedFilters.facets)
    const sorting = JSON.stringify(selectedFilters.sort)
    authAPICall(
      'productinfo',
      'categoryproducts',
      'GET',
      {
        slug: catSlug,
        popkey: publicRuntimeConfig.POP_KEY,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        filters,
        sort: sorting
      },
      {}
    ).then((data) => {
      setProductsArr([...data.response])
      setTotalRows(data.numFound)
      const newFacets = removeFilterKeys({ ...data.facets })
      setFilters(newFacets)
      setFilterLoader(false)
    })
  }, [selectedFilters])

  const renderItems = () => {
    if (productsArr.length) {
      return productsArr.map(item => <div id={item.slug} key={item.slug}>
        <ProductCard data={item} vertical={!grid} /></div>)
    }
  }

  const submitForm = (filters, sort) => {
    setSelectedFilters({
      facets: { ...filters },
      sort: { ...sort }
    })
    window.br_data = window.br_data || {}
    window.br_data = window.br_data || {}
    window.br_data.acct_id = publicRuntimeConfig.BR_ID
    window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
    window.br_data.ptype = 'category'
    window.br_data.view_id = state.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
    window.br_data.user_id = state.user.UID || state.deviceId

    window.br_data.orig_ref_url = location.href
    try {
      window.BrTrk.getTracker().updateBrData(window.br_data)
      window.BrTrk.getTracker().logPageView()
    } catch (e) {
      console.log(e)
    }
  }

  const handleDismiss = () => dismiss()
  const [present, dismiss] = useIonModal(Filters, { handleDismiss, data: filters, incomingData: { ...selectedFilters.facets }, incomingSort: selectedFilters.sort, submitForm, amt: productsArr.length ? totalRows : 0, loading: showFilterLoader }
  )
  const toggleFilters = async () => {
    present({ cssClass: 'modal--filters', enterAnimation, leaveAnimation })
  }

  /* added dynamically page title and meta tag */
  useEffect(() => {
    document.title = category[0].name

    const metaTag = document.createElement('meta')
    metaTag.name = 'description'
    metaTag.content = `Browse items at pOpshelf in the ${category[0].name} category`

    document.head.appendChild(metaTag)

    return () => {
      document.head.removeChild(metaTag)
    }
  }, [])

  return (
    <div id="main-content" className="category-page">
      <Head>
        {catSlug === 'new-now' && (
          <meta property="category:published_time" content={category[0].modified} />
        )}
        {category[0].meta_no_index && <meta name="robots" content="noindex" />}
      </Head>
      <div className="category-page__container">
        <div className="category-page__container__header">
          <nav className='nav__breadcrumb' aria-label="Breadcrumb">
            <BreadCrumb props={category[0].breadcrumbs} />
          </nav>
          <GeneralImageHero
            image={category[0].image_url}
            alt={category[0].alt || category[0].name}
            mobileImage={category[0].mobile_image}
            marginTop={false}
          />
          <PageTitle title={category[0].name} />
          <div></div>
        </div>

        <AEMContent template={category[0]?.template} page={AEMPageType.category} />

        <div id="target-content-top">
          <ContentGroup position="top" />
        </div>

        <div className="category-page__selectors">
          <FilterCount results={totalRows} />
          <div className="category-page__selectors-container">
            <div className="category-page__selectors-icons">
              <button className={grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                <SVG name='plp-grid' label='grid view' size='md' />
              </button>
              <button className={!grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                <SVG name='plp-list' label='list view' size='md' />
              </button>
            </div>
            <button className={`filters__button ${filtersSet ? 'filters__button-bg' : ''}`} onClick={toggleFilters}>
              <p>{filtersSet ? 'Filtered' : 'Filters'}</p>
            </button>
          </div>
        </div>

        <div className={`product-list-page ${grid ? 'product-list-page__grid' : ''}`}>
          {renderItems()}
        </div>

        <div className="category-page__view-more">
          {(((totalRows > (offset + 24))) || animated) && (
            <button
              onClick={(e) => {
                if (!animated) {
                  setAnimated(true)
                  setOffset(offset + 24)
                }
              }}
              className={`btn__secondary${animated ? ' btn__animated' : ''}`}
            >
              VIEW MORE
            </button>
          )}
        </div>
        <div id="target-content-bottom">
          <ContentGroup position="bottom" />
        </div>
        <div>
          <Widget type="c" slug={catSlug} data={widget} />
        </div>
        <div className="category-page__seo">
          {!!productsArr.length && category[0].seo_copy && (
            <SEOCopy seoCopyHtml={category[0].seo_copy}></SEOCopy>
          )}
        </div>
        <BackToTop />
      </div>
    </div>
  )
}

export default Category

export const getServerSideProps = async ({ req, params }) => {
  const { publicRuntimeConfig } = getConfig()
  const categorySlugs = params.category.join()
  const catSlug = params.category.pop()

  const categoryRes = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/category?slugs=${categorySlugs}&popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )
  const category = await categoryRes.json()

  if (category.length > 0 && category[0].redirect_path) {
    return {
      redirect: {
        destination: category[0].redirect_path,
        permanent: true
      }
    }
  }

  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/categoryproducts?slug=${catSlug}&popkey=${publicRuntimeConfig.POP_KEY}&store=${publicRuntimeConfig.DEFAULT_STORE}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )

  const items = await res.json()

  if (!Object.keys(items).length) {
    return {
      notFound: true
    }
  }

  const widgetDataRes = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/recommended?slug=${catSlug}&type=c&store=${publicRuntimeConfig.DEFAULT_STORE}&popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )
  const widgetData = await widgetDataRes.json()

  if (category.redirect_path) {
    return {
      redirect: {
        destination: category.redirect_path,
        permanent: true
      }
    }
  }

  return {
    props: {
      products: items.response || [],
      category,
      numberOfResults: items.numFound || 0,
      widget: widgetData.body.data,
      catSlug
    }
  }
}
